/**
 * Carema: directional header (every page; mobile, tablet and desktop share this one state model).
 * Behaviour: the header leaves while the visitor moves down the page and comes back when the visitor moves up,
 * anywhere on the page. It reacts to net travel in a direction, never to single-pixel changes.
 *   VISIBLE -> HIDDEN : scroll > 24px AND at least 16px of net downward travel since the lowest point reached
 *   HIDDEN -> VISIBLE : at least 16px of net upward travel since the highest point reached, or scroll <= 4px
 * Travel is measured from the extreme point (valley while visible, peak while hidden), so momentum noise
 * (-1, +2, -2, +1 ...) never changes the state and slow, slightly jittery gestures still register.
 * JS only decides the state (one class on <html>); the movement is CSS (see carema-typography-authority.css).
 * Scope: every page that renders the theme header (.section-header), every width. Each page keeps its own header look (solid or transparent).
 * First hide: scroll > 24px. A header that does NOT overlay the content (every page except the transparent Home) leaves its own slot in the
 * document flow, so it is only hidden once that slot has scrolled out of view (no empty band where the header was).
 */
(function () {
  'use strict';

  var HIDE_AFTER = 24;
  var TOP_AT = 4;
  var DIST = 16;
  /* Home only: the header comes back after a deliberate scroll up (120px of upward travel), so small corrections while stopping
     never cover the controls under it. Every other page keeps DIST (16px). */
  var DIST_UP_HOME = 120;
  var HIDDEN = 'carema-header-hidden';
  var INSTANT = 'carema-header-no-anim';

  var root = document.documentElement;
  var hidden = false;
  var active = false;
  var ticking = false;
  var interacted = false;
  var wasOverlay = false;
  var hideAfter = HIDE_AFTER;
  var overlaysContent = true;
  var ref = 0;

  /* Scroll position normalised to [0, max]: iOS rubber-band reports negative values at the top and values past the end at the bottom. */
  function scrollPos() {
    var v = window.pageYOffset || root.scrollTop || 0;
    if (v <= 0) return 0;
    var limit = root.scrollHeight - window.innerHeight;
    return limit > 0 && v > limit ? limit : v;
  }

  /* While a header-controlled overlay is open the state is frozen: the page behind it must not move the header.
     Desktop adds two guards: an open dropdown / mega menu (the theme marks it with section-header--nav-open) and keyboard
     focus inside the header (body.tab-used). Plain hover over the header does not freeze anything. */
  function overlayOpen() {
    var b = document.body;
    if (!b) return false;
    if (b.classList.contains('reveal-mobile-nav') || b.classList.contains('show-search')) return true;
    if (root.classList.contains('gallery-viewer-open')) return true;
    var header = document.querySelector('.section-header');
    if (header) {
      if (header.classList.contains('section-header--nav-open')) return true;
      if (b.classList.contains('tab-used') && header.contains(document.activeElement)) return true;
    }
    var ov = document.querySelector('.js-overlay');
    return !!(ov && ov.classList.contains('is-visible'));
  }

  /* The Home header is transparent and overlaps the Hero (negative bottom margin), so nothing blank can show when it leaves.
     Elsewhere the header owns a slot in the flow: wait until the content below it has reached the top of the viewport. */
  function measure() {
    var header = document.querySelector('.section-header');
    hideAfter = HIDE_AFTER;
    overlaysContent = true;
    if (!header) return;
    overlaysContent = (parseFloat(window.getComputedStyle(header).marginBottom) || 0) < 0;
    if (!overlaysContent) {
      var main = document.getElementById('content');
      if (main) {
        var slotEnd = Math.ceil(main.getBoundingClientRect().top + (window.pageYOffset || 0));
        if (slotEnd > hideAfter) hideAfter = slotEnd;
      }
    }
  }

  function upDist() {
    var b = document.body;
    return b && b.classList.contains('template-index') ? DIST_UP_HOME : DIST;
  }

  function setHidden(next) {
    if (next === hidden) return;
    hidden = next;
    /* Before any user input (restored scroll position, anchor jump) change state without animating. */
    if (!interacted) {
      root.classList.add(INSTANT);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { root.classList.remove(INSTANT); });
      });
    }
    root.classList.toggle(HIDDEN, next);
  }

  function evaluate() {
    ticking = false;
    if (!active) { setHidden(false); return; }
    if (overlayOpen()) { wasOverlay = true; return; }
    var y = scrollPos();
    if (wasOverlay) { wasOverlay = false; ref = y; }   /* overlay closed: re-base on the current position, infer no direction */
    if (y <= TOP_AT) { ref = y; setHidden(false); return; }
    if (hidden) {
      if (!overlaysContent && y < hideAfter) { ref = y; setHidden(false); return; }   /* never leave the empty slot exposed */
      if (y > ref) ref = y;
      else if (ref - y >= upDist()) { ref = y; setHidden(false); }
    } else {
      if (y < ref) ref = y;
      else if (y > hideAfter && y - ref >= DIST) { ref = y; setHidden(true); }
    }
  }

  function request() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(evaluate);
  }

  function sync() {
    active = !!document.querySelector('.section-header');
    measure();
    wasOverlay = false;
    var y = scrollPos();
    ref = y;
    if (!active || y <= TOP_AT) { setHidden(false); return; }
    if (y > hideAfter && !overlayOpen()) setHidden(true);
  }

  function onResize() { measure(); request(); }

  function markInteracted() { interacted = true; }
  ['touchstart', 'wheel', 'mousedown', 'keydown'].forEach(function (type) {
    window.addEventListener(type, markInteracted, { passive: true, once: true, capture: true });
  });

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('pageshow', sync);
  window.addEventListener('load', sync);
  window.addEventListener('resize', onResize);

  /* Re-evaluate when an overlay closes (no scroll event is fired by that). */
  document.addEventListener('on:cart-drawer:after-close', request);
  document.addEventListener('on:quickbuy:after-close', request);
  if (typeof MutationObserver === 'function') {
    var mo = new MutationObserver(request);
    if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    var ov = document.querySelector('.js-overlay');
    if (ov) mo.observe(ov, { attributes: true, attributeFilter: ['class'] });
  }

  /* Keyboard focus entering the hidden header brings it back (nothing focusable stays off-screen). */
  document.addEventListener('focusin', function (e) {
    if (!hidden || !active) return;
    var header = document.querySelector('.section-header');
    if (!header || !header.contains(e.target)) return;
    var visible = false;
    try { visible = e.target.matches(':focus-visible'); } catch (err) { visible = false; }
    if (visible) { ref = scrollPos(); setHidden(false); }
  });

  sync();
})();
